import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { onrampAPI } from '../../services/api';
import { responseData } from '../../services/apiResponse';

function reconcileSelection(data, currentOnramp, currentMethods) {
  const onramps = data?.onramps || [];
  const selectedOnramp = onramps.some((record) => record.onramp_id === currentOnramp)
    ? currentOnramp
    : onramps[0]?.onramp_id || null;
  const methods = {};
  onramps.forEach((record) => {
    const previous = currentMethods[record.onramp_id];
    methods[record.onramp_id] = record.payment_methods.some((method) => method.payment_method === previous)
      ? previous
      : record.selected_payment_method || record.payment_methods[0]?.payment_method;
  });
  return { selectedOnramp, methods };
}

const useOnrampQuotes = ({ payload, enabled }) => {
  const payloadKey = JSON.stringify(payload || {});
  const stablePayload = useMemo(() => JSON.parse(payloadKey), [payloadKey]);
  const [response, setResponse] = useState(null);
  const [selectedOnrampId, setSelectedOnrampId] = useState(null);
  const [selectedPaymentMethods, setSelectedPaymentMethods] = useState({});
  const [loading, setLoading] = useState(Boolean(enabled));
  const [refreshing, setRefreshing] = useState(false);
  const [refreshProgress, setRefreshProgress] = useState(null);
  const [refreshCycle, setRefreshCycle] = useState(0);
  const [stale, setStale] = useState(false);
  const [error, setError] = useState(null);
  const responseRef = useRef(null);
  const selectionRef = useRef({ onramp: null, methods: {} });
  const requestSequence = useRef(0);
  const inFlightRef = useRef(null);
  const pollTimerRef = useRef(null);
  const countdownTimerRef = useRef(null);

  useEffect(() => {
    selectionRef.current = { onramp: selectedOnrampId, methods: selectedPaymentMethods };
  }, [selectedOnrampId, selectedPaymentMethods]);

  const refresh = useCallback(() => {
    if (!enabled) return Promise.resolve(null);
    if (inFlightRef.current) return inFlightRef.current;
    const sequence = ++requestSequence.current;
    const hasData = Boolean(responseRef.current);
    if (hasData) setRefreshing(true);
    else setLoading(true);
    const request = (async () => {
      try {
        const apiResponse = await onrampAPI.getQuotes(stablePayload);
        if (sequence !== requestSequence.current) return null;
        const data = responseData(apiResponse);
        const nextSelection = reconcileSelection(
          data,
          selectionRef.current.onramp,
          selectionRef.current.methods,
        );
        responseRef.current = data;
        selectionRef.current = { onramp: nextSelection.selectedOnramp, methods: nextSelection.methods };
        setResponse(data);
        setSelectedOnrampId(nextSelection.selectedOnramp);
        setSelectedPaymentMethods(nextSelection.methods);
        setStale(false);
        setError(null);
        setRefreshCycle((cycle) => cycle + 1);
        return data;
      } catch (requestError) {
        if (sequence !== requestSequence.current) return null;
        if (responseRef.current) setStale(true);
        else setError(requestError);
        return null;
      } finally {
        if (sequence === requestSequence.current) {
          setLoading(false);
          setRefreshing(false);
        }
        if (inFlightRef.current === request) inFlightRef.current = null;
      }
    })();
    inFlightRef.current = request;
    return request;
  }, [enabled, stablePayload]);

  useEffect(() => {
    if (!enabled) return undefined;
    void refresh();
    return () => {
      requestSequence.current += 1;
      inFlightRef.current = null;
    };
  }, [enabled, payloadKey, refresh]);

  useEffect(() => {
    if (!enabled || !response) return undefined;
    const seconds = Math.max(5, Number(response.refresh_after_seconds || 15));
    const durationMs = seconds * 1000;
    const clearTimer = () => {
      if (pollTimerRef.current) window.clearTimeout(pollTimerRef.current);
      if (countdownTimerRef.current) window.clearInterval(countdownTimerRef.current);
      pollTimerRef.current = null;
      countdownTimerRef.current = null;
    };
    const schedule = () => {
      clearTimer();
      if (!document.hidden) {
        const deadline = Date.now() + durationMs;
        setRefreshProgress(1);
        countdownTimerRef.current = window.setInterval(() => {
          const remainingMs = Math.max(0, deadline - Date.now());
          setRefreshProgress(remainingMs / durationMs);
        }, 100);
        pollTimerRef.current = window.setTimeout(() => {
          if (countdownTimerRef.current) window.clearInterval(countdownTimerRef.current);
          countdownTimerRef.current = null;
          setRefreshProgress(0);
          void refresh();
        }, durationMs);
      } else {
        setRefreshProgress(null);
      }
    };
    schedule();
    const handleVisibility = () => {
      if (document.hidden) {
        clearTimer();
        setRefreshProgress(null);
      }
      else void refresh();
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      clearTimer();
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [enabled, response, refresh, refreshCycle]);

  const selectOnramp = useCallback((onrampID) => {
    selectionRef.current = { ...selectionRef.current, onramp: onrampID };
    setSelectedOnrampId(onrampID);
  }, []);
  const selectPaymentMethod = useCallback((onrampID, paymentMethod) => {
    setSelectedPaymentMethods((current) => {
      const next = { ...current, [onrampID]: paymentMethod };
      selectionRef.current = { ...selectionRef.current, methods: next };
      return next;
    });
  }, []);

  return {
    response,
    selectedOnrampId,
    selectedPaymentMethods,
    loading,
    refreshing,
    refreshProgress,
    stale,
    error,
    refresh,
    selectOnramp,
    selectPaymentMethod,
  };
};

export default useOnrampQuotes;
